export function splitName(fullName: string): {
  firstName: string;
  surName: string;
} {
  const trimmedName = fullName.trim();
  const parts = trimmedName.split(" ");

  if (parts.length === 1) {
    return { firstName: parts[0], surName: "" };
  }

  const firstName = parts[0];
  const surName = parts.slice(1).join(" ");

  return { firstName, surName };
}

const ROMAN_SUFFIX_PATTERN = /^(ii|iii|iv|v|vi|vii|viii|ix|x)$/;

export function toTitleCase(name: string): string {
  const normalized = name.trim().toLowerCase().replace(/\s+/g, " ");
  const lastWord = normalized.split(" ").pop() ?? "";
  // Only the final word can be a generational suffix; "vi" and "x" are also
  // names and middle initials elsewhere in a name.
  const romanSuffix = ROMAN_SUFFIX_PATTERN.test(lastWord) ? lastWord : null;

  return normalized
    .split(/(\s+|-)/g)
    .map((word) => {
      if (!word || word === " " || word === "-") {
        return word;
      } else if (/^([a-z]\.)+$/.test(word)) {
        return word.toUpperCase();
      } else if (word.includes("'")) {
        return word
          .split("'")
          .map((part, i) =>
            i > 0 && part === "s" ? part : part.charAt(0).toUpperCase() + part.slice(1),
          )
          .join("'");
      } else if (word.startsWith("mc") && word.length > 2) {
        return "Mc" + word.charAt(2).toUpperCase() + word.slice(3);
      } else if (
        word.startsWith("mac") &&
        word.length > 3 &&
        /^[acdghklmnprstw]/.test(word.slice(3))
      ) {
        return "Mac" + word.charAt(3).toUpperCase() + word.slice(4);
      } else if (romanSuffix && word === romanSuffix) {
        return word.toUpperCase();
      } else {
        return word.charAt(0).toUpperCase() + word.slice(1);
      }
    })
    .join("");
}

/**
 * Normalizes a user-entered name for storage and display. Always trims and
 * collapses whitespace, but only applies title case when the input carries no
 * case signal (all lower or all upper). Mixed case such as "LaTanya" or
 * "van der Berg" is a deliberate choice and is preserved as typed.
 */
export function normalizeName(name: string): string {
  const collapsed = name.trim().replace(/\s+/g, " ");
  const letters = collapsed.replace(/[^\p{L}]/gu, "");
  const hasNoCaseSignal =
    !!letters && (letters === letters.toLowerCase() || letters === letters.toUpperCase());

  return hasNoCaseSignal ? toTitleCase(collapsed) : collapsed;
}

const NAME_FIELDS = ["first_name", "last_name", "full_name", "middle_name"] as const;

/**
 * Returns a copy of a database row payload with any present human-name fields
 * normalized. Fields that are absent, null or empty are left untouched.
 */
export function normalizeNameFields<T extends object>(payload: T): T {
  const result = { ...payload } as Record<string, unknown>;

  for (const field of NAME_FIELDS) {
    const value = result[field];
    if (typeof value === "string" && value.trim()) {
      result[field] = normalizeName(value);
    }
  }

  return result as T;
}

export function buildFullName(first: string, middle: string, last: string): string {
  return [first, middle, last].filter(Boolean).join(" ").trim();
}

/**
 * Extracts the last name from a full-name token array by stripping the first
 * name (index 0) and a known middle name (which may be multi-word).
 */
export function extractLastName(fullNameParts: string[], middleName: string): string {
  if (!middleName) return fullNameParts.slice(1).join(" ") || "";
  const middleParts = middleName.split(/\s+/);
  const startIndex = 1;
  const middleMatches = middleParts.every(
    (part, i) => fullNameParts[startIndex + i]?.toLowerCase() === part.toLowerCase(),
  );
  if (middleMatches) {
    return fullNameParts.slice(startIndex + middleParts.length).join(" ") || "";
  }
  return fullNameParts.slice(1).join(" ") || "";
}

export function isValidFullName(value: string): boolean {
  const words = value.trim().split(/\s+/);
  return words.length >= 2 && words.every((word) => word.length > 0);
}

export function getFirstName(fullName: string | undefined): string {
  if (!fullName || typeof fullName !== "string") return "their";
  return fullName.split(" ")[0] || "their";
}

const NAME_SUFFIX_PATTERN = /,?\s+(jr\.?|sr\.?|ii|iii|iv|v|vi|vii|viii|esq\.?)$/i;

export function stripNameSuffix(name: string): string {
  return name.replace(NAME_SUFFIX_PATTERN, "").trim();
}

export function splitFullNameParts(fullName: string): {
  firstName: string;
  middleName: string;
  lastName: string;
} {
  const suffix = fullName.trim().match(NAME_SUFFIX_PATTERN)?.[1] ?? "";
  const parts = stripNameSuffix(fullName).split(/\s+/).filter(Boolean);
  const lastName =
    parts.length > 1 ? [parts[parts.length - 1], suffix].filter(Boolean).join(" ") : "";
  return {
    firstName: parts[0] ?? "",
    middleName: parts.length > 2 ? parts.slice(1, -1).join(" ") : "",
    lastName,
  };
}
